import { inject, Injectable } from "@angular/core";
import { UserInfoService } from "@lib/core/services/user-info.service";
import { canRoleAccess, getRightsOnObject, hasRightsOnObject } from "@lib/core/utils/roles.utils";
import { map, Observable, of } from "rxjs";
import { OperationType } from "@graphql-generated/graphql";

@Injectable({
    providedIn: "root",
})
export class UserRolesService {
    private userInfoService = inject(UserInfoService);

    canI(action: "CREATE" | "UPDATE" | "READ" | "DELETE", objectName: string): Observable<boolean> {
        return this.userInfoService.getUserInfo().pipe(
            map(userInfo => canRoleAccess(userInfo.possedeRoles, action, objectName)),
        );
    }

    canIAll(action: "CREATE" | "UPDATE" | "READ" | "DELETE", objectNames: string[]): Observable<{
        permission: string;
        canAccess: boolean;
    }[]> {
        return this.userInfoService.getUserInfo().pipe(
            map((userInfo) => {
                return objectNames.map((objectName) => {
                    return {
                        permission: objectName,
                        canAccess: canRoleAccess(userInfo.possedeRoles, action, objectName),
                    };
                });
            }),
        );
    }

    getAuthorizedActions(objectName: string): Observable<OperationType[]> {
        return this.userInfoService.getUserInfo().pipe(
            map(userInfo => getRightsOnObject(userInfo.possedeRoles, objectName)),
        );
    }

    hasPermissions(objectsPermissionsNeeded: string[]): Observable<boolean> {
        return this.userInfoService.getUserInfo().pipe(
            map((userInfo) => {
                return objectsPermissionsNeeded.every(objectPermissionNeeded =>
                    hasRightsOnObject(userInfo.possedeRoles, objectPermissionNeeded),
                );
            }),
        );
    }

    getRoles() {
        return this.userInfoService.getUserInfo().pipe(
            map(userInfo => userInfo.possedeRoles),
        );
    }

    hasRole(roleName: string) {
        return this.getRoles().pipe(
            map(userRoles => userRoles.some(role => role.name === roleName)),
        );
    }

    hasRoles(roles: string[]): Observable<boolean> {
        if (roles.length === 0) {
            return of(true);
        }
        return this.getRoles().pipe(
            map(userRoles =>
                userRoles.some(role =>
                    roles.some(roleName => roleName === role.name),
                ),
            ),
        );
    }

    isAdmin(): Observable<boolean> {
        return this.hasRoles(["Administrator"]);
    }
}
